import type { ProductReview } from '../mocks/productReviews';

const PENDING_KEY = 'sipdash_reviews_pending';

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface ModerationReview extends ProductReview {
  beverageName: string;
  submittedAt: string;
  status: ReviewStatus;
}

// ── Read ──────────────────────────────────────────────────────────────────

export function getAllModerationReviews(): ModerationReview[] {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]') as ModerationReview[];
  } catch {
    return [];
  }
}

export function getPendingReviews(): ModerationReview[] {
  return getAllModerationReviews().filter((r) => r.status === 'pending');
}

export function getPendingReviewsCount(): number {
  return getPendingReviews().length;
}

// ── Write ─────────────────────────────────────────────────────────────────

export function submitReviewForModeration(
  review: ProductReview,
  beverageName: string
): void {
  const all = getAllModerationReviews();
  const entry: ModerationReview = {
    ...review,
    beverageName,
    submittedAt: new Date().toISOString(),
    status: 'pending',
  };
  localStorage.setItem(PENDING_KEY, JSON.stringify([entry, ...all]));
}

export function approveReview(reviewId: string): void {
  const all = getAllModerationReviews();
  const review = all.find((r) => r.id === reviewId);
  if (!review) return;

  // Mark as approved in the moderation queue
  const updated = all.map((r) =>
    r.id === reviewId ? { ...r, status: 'approved' as ReviewStatus } : r
  );
  localStorage.setItem(PENDING_KEY, JSON.stringify(updated));

  // Persist into the product's public review list
  const key = `sipdash_reviews_${review.beverageId}`;
  try {
    const existing: ProductReview[] = JSON.parse(localStorage.getItem(key) || '[]');
    if (!existing.find((r) => r.id === reviewId)) {
      const approved: ProductReview = {
        id: review.id,
        beverageId: review.beverageId,
        userName: review.userName,
        avatar: review.avatar,
        rating: review.rating,
        title: review.title,
        comment: review.comment,
        date: review.date,
        verified: review.verified,
        helpful: review.helpful,
      };
      localStorage.setItem(key, JSON.stringify([approved, ...existing]));
    }
  } catch {
    // ignore
  }
}

export function rejectReview(reviewId: string): void {
  const all = getAllModerationReviews();
  const updated = all.map((r) =>
    r.id === reviewId ? { ...r, status: 'rejected' as ReviewStatus } : r
  );
  localStorage.setItem(PENDING_KEY, JSON.stringify(updated));
}

export function deleteReviewFromModeration(reviewId: string): void {
  const all = getAllModerationReviews().filter((r) => r.id !== reviewId);
  localStorage.setItem(PENDING_KEY, JSON.stringify(all));
}
